import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBusinessContext } from "@/providers/business-provider";
import { useAuth } from "@/providers/auth-provider";
import { useLoading } from "@/providers/loading-provider";
import { getInitials } from "@/lib/utils";
import {
  Building2, Plus, ArrowRight, CreditCard, Settings, Clock,
} from "lucide-react";

export function WelcomeDashboard() {
  const { user } = useAuth();
  const { businesses, setActiveBusinessId, isLoading: isLoadingBusinesses } = useBusinessContext();
  const { navigateTo, startLoading } = useLoading();

  const { data: subscription } = useQuery<any>({
    queryKey: ["/api/billing/subscription"],
    queryFn: async () => {
      const res = await fetch("/api/billing/subscription", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60_000,
  });

  const { data: licenseInfo } = useQuery<any>({
    queryKey: ["/api/billing/licenses"],
    queryFn: async () => {
      const res = await fetch("/api/billing/licenses", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60_000,
  });

  const handleSelectBusiness = async (business: any) => {
    startLoading();
    await setActiveBusinessId(business.id);
    navigateTo(`/dashboard?business=${business.id}&t=${Date.now()}`);
  };

  const handleCreateBusiness = () => {
    startLoading();
    navigateTo("/business/create");
  };

  const firstName = user?.fullName?.split(" ")[0] || user?.firstName || user?.username || "there";

  function timeAgo(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;
    const ms = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  const planLabel = subscription?.isTrial ? "Free Trial" : (subscription?.planName || null);
  // Always derive `used` from the live businesses list — that's the actual
  // number of licenses consumed regardless of stale subscription rows.
  // For `total`, prefer the API value but never let it drop below `used`
  // (otherwise the badge can read "3 of 1" when the most-recent
  // business_subscriptions row has a stale license_quantity).
  const licensesUsed = businesses.length;
  const apiTotal =
    licenseInfo?.licenseQuantity ?? licenseInfo?.licensesTotal ?? 0;
  const licensesTotal = Math.max(apiTotal, licensesUsed, 1);
  const licensePct = Math.min(100, Math.round((licensesUsed / Math.max(licensesTotal, 1)) * 100));

  if (isLoadingBusinesses) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-6">
        <div className="h-8 w-56 bg-gray-100 animate-pulse rounded mb-2" />
        <div className="h-4 w-80 bg-gray-100 animate-pulse rounded mb-10" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map(i => <div key={i} className="h-[88px] bg-gray-100 animate-pulse rounded-2xl" />)}
        </div>
      </div>
    );
  }

  // ── No businesses at all ──────────────────────────────────────────
  if (businesses.length === 0) {
    return (
      <div className="max-w-md mx-auto py-24 px-6 text-center">
        <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
          <Building2 className="h-7 w-7 text-blue-600" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-1.5">
          Welcome, {firstName}
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          Create your first business to start bookkeeping with ZentroBooks.
        </p>
        <Button onClick={handleCreateBusiness} className="h-11 px-7 gap-2 rounded-xl shadow-sm">
          <Plus className="h-4 w-4" /> Create Your First Business
        </Button>
      </div>
    );
  }

  // ── Businesses exist — selection screen ───────────────────────────
  return (
    <div className="max-w-5xl mx-auto py-10 px-6">
      {/* Header + actions */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Welcome back, {firstName}</h1>
          <p className="text-sm text-gray-400 mt-1">Select a business to continue or create a new one.</p>
        </div>
        <div className="flex items-center gap-2 mt-4 sm:mt-0">
          <Button variant="outline" size="sm" onClick={handleCreateBusiness} className="gap-1.5 rounded-lg text-xs h-8">
            <Plus className="h-3.5 w-3.5" /> New Business
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigateTo("/subscription")} className="gap-1.5 rounded-lg text-xs h-8">
            <Settings className="h-3.5 w-3.5" /> Manage Plan
          </Button>
        </div>
      </div>

      {/* Business grid — sorted by last accessed */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {[...businesses]
          .sort((a: any, b: any) => {
            const ta = a.last_accessed_at || a.lastAccessedAt || "";
            const tb = b.last_accessed_at || b.lastAccessedAt || "";
            return tb.localeCompare(ta);
          })
          .map((business: any, idx: number) => {
            const pubId = (business as any).publicId || (business as any).public_id;
            const lastAccess = business.last_accessed_at || business.lastAccessedAt;
            const ago = timeAgo(lastAccess);
            const isLastUsed = idx === 0 && !!lastAccess;
            const displayName = business.name || "Unnamed Business";
            return (
              <button
                key={business.id}
                onClick={() => handleSelectBusiness(business)}
                className={`group text-left rounded-2xl border bg-white p-5 shadow-sm hover:shadow-md hover:border-blue-200 hover:scale-[1.02] transition-all duration-150 ${isLastUsed ? "border-blue-200 ring-1 ring-blue-100" : "border-gray-200"}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0 group-hover:bg-blue-100 transition-colors overflow-hidden">
                    {business.logo_url || business.logoUrl
                      ? <img src={business.logo_url || business.logoUrl} alt={displayName} className="h-full w-full object-cover rounded-xl" />
                      : getInitials(displayName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                        {displayName}
                      </h3>
                      {isLastUsed && <Badge className="text-[9px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200 font-medium shrink-0">Last used</Badge>}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                      {pubId ? `ID: ${pubId}` : (business.industry || "Business")}
                    </p>
                    {ago && (
                      <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5" /> {ago}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                </div>
              </button>
            );
          })}

        {/* Create new business card */}
        <button
          onClick={handleCreateBusiness}
          className="group rounded-2xl border border-gray-200 bg-gray-50 p-5 hover:bg-blue-50/60 hover:border-blue-200 hover:shadow-sm transition-all duration-150 flex items-center gap-4"
        >
          <div className="h-11 w-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 group-hover:border-blue-200 transition-colors">
            <Plus className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-600 group-hover:text-blue-600 transition-colors">Create New Business</h3>
            <p className="text-[11px] text-gray-400">Set up a new company</p>
          </div>
        </button>
      </div>

      {/* Subscription overview */}
      {planLabel && (
        <Card className="rounded-2xl shadow-sm border-gray-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{planLabel}</span>
                    {subscription?.isTrial && (
                      <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 font-medium px-1.5 py-0">
                        Trial
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {licensesUsed} of {licensesTotal} business licenses used
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigateTo("/subscription")} className="text-xs rounded-lg h-8">
                Manage Plan
              </Button>
            </div>
            {/* License progress bar */}
            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${licensePct >= 100 ? "bg-red-500" : licensePct >= 75 ? "bg-amber-500" : "bg-blue-500"}`}
                style={{ width: `${licensePct}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
